import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { UserPlus, Edit2, ToggleLeft, ToggleRight, ArrowLeft, X, Check, Plus } from 'lucide-react';

const PROJETOS = ['C6', 'Algar'];
const formUsuarioInicial = { nome: '', email: '', senha: '', agent_name: '', perfil: 'operador', equipe: '', projeto: 'C6' };
const formEquipeInicial = { nome: '', projeto: 'C6' };

export default function Usuarios() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [aba, setAba] = useState('usuarios');
  const [usuarios, setUsuarios] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ aberto: false, item: null, tipo: null });
  const [formUsuario, setFormUsuario] = useState(formUsuarioInicial);
  const [formEquipe, setFormEquipe] = useState(formEquipeInicial);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [busca, setBusca] = useState('');

  const carregar = useCallback(async () => {
    try {
      const [resU, resE] = await Promise.all([api.get('/usuarios'), api.get('/equipes')]);
      setUsuarios(resU.data);
      setEquipes(resE.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (usuario?.perfil !== 'administrador') { navigate('/dashboard'); return; }
    carregar();
  }, [usuario, navigate, carregar]);

  const abrirModalUsuario = (u = null) => {
    setFormUsuario(u ? { nome: u.nome, email: u.email, senha: '', agent_name: u.agent_name, perfil: u.perfil, equipe: u.equipe || '', projeto: u.projeto || 'C6' } : formUsuarioInicial);
    setErro('');
    setModal({ aberto: true, item: u, tipo: 'usuario' });
  };

  const abrirModalEquipe = (e = null) => {
    setFormEquipe(e ? { nome: e.nome, projeto: e.projeto || 'C6' } : formEquipeInicial);
    setErro('');
    setModal({ aberto: true, item: e, tipo: 'equipe' });
  };

  const salvarUsuario = async () => {
    if (!formUsuario.nome || !formUsuario.email || !formUsuario.agent_name) {
      setErro('Nome, email e nome do agente são obrigatórios'); return;
    }
    if (!modal.item && !formUsuario.senha) {
      setErro('Senha é obrigatória para novos usuários'); return;
    }
    setSalvando(true); setErro('');
    try {
      if (modal.item) {
        const res = await api.put(`/usuarios/${modal.item.id}`, formUsuario);
        setUsuarios(prev => prev.map(u => u.id === modal.item.id ? res.data : u));
      } else {
        const res = await api.post('/usuarios', formUsuario);
        setUsuarios(prev => [...prev, res.data]);
      }
      setModal({ aberto: false, item: null, tipo: null });
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao salvar');
    } finally { setSalvando(false); }
  };

  const salvarEquipe = async () => {
    if (!formEquipe.nome) { setErro('Nome é obrigatório'); return; }
    setSalvando(true); setErro('');
    try {
      if (modal.item) {
        const res = await api.put(`/equipes/${modal.item.id}`, formEquipe);
        setEquipes(prev => prev.map(e => e.id === modal.item.id ? res.data : e));
      } else {
        const res = await api.post('/equipes', formEquipe);
        setEquipes(prev => [...prev, res.data]);
      }
      setModal({ aberto: false, item: null, tipo: null });
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao salvar');
    } finally { setSalvando(false); }
  };

  const alternarStatusUsuario = async (u) => {
    try {
      const res = await api.patch(`/usuarios/${u.id}/status`, { ativo: !u.ativo });
      setUsuarios(prev => prev.map(x => x.id === u.id ? { ...x, ativo: res.data.ativo } : x));
    } catch (err) { console.error(err); }
  };

  const alternarStatusEquipe = async (e) => {
    try {
      const res = await api.patch(`/equipes/${e.id}/status`, { ativo: !e.ativo });
      setEquipes(prev => prev.map(x => x.id === e.id ? { ...x, ativo: res.data.ativo } : x));
    } catch (err) { console.error(err); }
  };

  const equipesAtivas = equipes.filter(e => e.ativo);
  const usuariosFiltrados = usuarios.filter(u =>
    u.nome.toLowerCase().includes(busca.toLowerCase()) ||
    u.email.toLowerCase().includes(busca.toLowerCase()) ||
    (u.equipe?.toLowerCase() || '').includes(busca.toLowerCase())
  );

  return (
    <div style={s.page}>
      {/* HEADER */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          <button style={s.btnVoltar} onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} /> Voltar
          </button>
          <div>
            <h1 style={s.titulo}>Gerenciar Usuários</h1>
            <p style={s.subtitulo}>{usuarios.length} usuário{usuarios.length !== 1 ? 's' : ''} · {equipes.length} equipe{equipes.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button style={s.btnNovo} onClick={() => aba === 'usuarios' ? abrirModalUsuario() : abrirModalEquipe()}>
          {aba === 'usuarios' ? <><UserPlus size={16} /> Novo Usuário</> : <><Plus size={16} /> Nova Equipe</>}
        </button>
      </div>

      {/* ABAS */}
      <div style={s.abas}>
        <button style={{...s.aba, ...(aba === 'usuarios' ? s.abaAtiva : {})}} onClick={() => setAba('usuarios')}>Usuários</button>
        <button style={{...s.aba, ...(aba === 'equipes' ? s.abaAtiva : {})}} onClick={() => setAba('equipes')}>Equipes</button>
      </div>

      {loading ? <p style={s.loadingText}>Carregando...</p> : aba === 'usuarios' ? (
        <>
          <input style={s.busca} placeholder="Buscar por nome, email ou equipe..." value={busca} onChange={e => setBusca(e.target.value)} />
          <div style={s.tabela}>
            <div style={s.tabelaHeader}>
              <span style={{...s.th, flex: 2}}>Nome</span>
              <span style={{...s.th, flex: 2}}>Email</span>
              <span style={s.th}>Agente no Banco</span>
              <span style={s.th}>Perfil</span>
              <span style={s.th}>Equipe</span>
              <span style={s.th}>Projeto</span>
              <span style={s.th}>Status</span>
              <span style={{...s.th, textAlign: 'right'}}>Ações</span>
            </div>
            {usuariosFiltrados.map(u => (
              <div key={u.id} style={{...s.row, opacity: u.ativo ? 1 : 0.5}}>
                <span style={{...s.td, flex: 2, color: '#f1f5f9', fontWeight: 500}}>{u.nome}</span>
                <span style={{...s.td, flex: 2}}>{u.email}</span>
                <span style={s.td}>{u.agent_name}</span>
                <span style={s.td}><span style={{...s.badge, background: u.perfil === 'administrador' ? '#6366f122' : u.perfil === 'supervisor' ? '#F0702022' : '#10b98122', color: u.perfil === 'administrador' ? '#818cf8' : u.perfil === 'supervisor' ? '#F07020' : '#10b981'}}>{u.perfil}</span></span>
                <span style={s.td}>{u.equipe || '—'}</span>
                <span style={s.td}><span style={{...s.badge, background: '#3b82f622', color: '#60a5fa'}}>{u.projeto || 'C6'}</span></span>
                <span style={s.td}><span style={{...s.badge, background: u.ativo ? '#10b98122' : '#ef444422', color: u.ativo ? '#10b981' : '#ef4444'}}>{u.ativo ? 'Ativo' : 'Inativo'}</span></span>
                <span style={{...s.td, display: 'flex', gap: 8, justifyContent: 'flex-end'}}>
                  <button style={s.btnAcao} onClick={() => abrirModalUsuario(u)}><Edit2 size={14} color="#94a3b8" /></button>
                  <button style={s.btnAcao} onClick={() => alternarStatusUsuario(u)}>
                    {u.ativo ? <ToggleRight size={18} color="#10b981" /> : <ToggleLeft size={18} color="#ef4444" />}
                  </button>
                </span>
              </div>
            ))}
            {usuariosFiltrados.length === 0 && <p style={s.vazio}>Nenhum usuário encontrado</p>}
          </div>
        </>
      ) : (
        <div style={s.tabela}>
          <div style={s.tabelaHeader}>
            <span style={{...s.th, flex: 2}}>Nome da Equipe</span>
            <span style={s.th}>Projeto</span>
            <span style={s.th}>Status</span>
            <span style={{...s.th, textAlign: 'right'}}>Ações</span>
          </div>
          {equipes.map(e => (
            <div key={e.id} style={{...s.row, opacity: e.ativo ? 1 : 0.5}}>
              <span style={{...s.td, flex: 2, color: '#f1f5f9', fontWeight: 500}}>{e.nome}</span>
              <span style={s.td}><span style={{...s.badge, background: '#3b82f622', color: '#60a5fa'}}>{e.projeto || 'C6'}</span></span>
              <span style={s.td}><span style={{...s.badge, background: e.ativo ? '#10b98122' : '#ef444422', color: e.ativo ? '#10b981' : '#ef4444'}}>{e.ativo ? 'Ativa' : 'Inativa'}</span></span>
              <span style={{...s.td, display: 'flex', gap: 8, justifyContent: 'flex-end'}}>
                <button style={s.btnAcao} onClick={() => abrirModalEquipe(e)}><Edit2 size={14} color="#94a3b8" /></button>
                <button style={s.btnAcao} onClick={() => alternarStatusEquipe(e)}>
                  {e.ativo ? <ToggleRight size={18} color="#10b981" /> : <ToggleLeft size={18} color="#ef4444" />}
                </button>
              </span>
            </div>
          ))}
          {equipes.length === 0 && <p style={s.vazio}>Nenhuma equipe cadastrada</p>}
        </div>
      )}

      {/* MODAL USUÁRIO */}
      {modal.aberto && modal.tipo === 'usuario' && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitulo}>{modal.item ? 'Editar Usuário' : 'Novo Usuário'}</h2>
              <button style={s.btnFechar} onClick={() => setModal({ aberto: false, item: null, tipo: null })}><X size={18} /></button>
            </div>
            <div style={s.modalBody}>
              <div style={s.campo}>
                <label style={s.label}>Nome completo *</label>
                <input style={s.input} value={formUsuario.nome} onChange={e => setFormUsuario({...formUsuario, nome: e.target.value})} placeholder="Ex: João Silva" />
              </div>
              <div style={s.campo}>
                <label style={s.label}>Email *</label>
                <input style={s.input} type="email" value={formUsuario.email} onChange={e => setFormUsuario({...formUsuario, email: e.target.value})} placeholder="joao@empresa.com" />
              </div>
              <div style={s.campo}>
                <label style={s.label}>Nome do agente no banco *</label>
                <input style={s.input} value={formUsuario.agent_name} onChange={e => setFormUsuario({...formUsuario, agent_name: e.target.value})} placeholder="Exatamente como aparece na tabela" />
                <span style={s.dica}>⚠️ Deve ser idêntico ao campo "colaborador/vendedor" no banco</span>
              </div>
              <div style={s.campoGrid}>
                <div style={s.campo}>
                  <label style={s.label}>Perfil</label>
                  <select style={s.input} value={formUsuario.perfil} onChange={e => setFormUsuario({...formUsuario, perfil: e.target.value})}>
                    <option value="operador">Operador</option>
                    <option value="administrador">Administrador</option>
                    <option value="supervisor">Supervisor</option>
                  </select>
                </div>
                <div style={s.campo}>
                  <label style={s.label}>Projeto</label>
                  <select style={s.input} value={formUsuario.projeto} onChange={e => setFormUsuario({...formUsuario, projeto: e.target.value, equipe: ''})}>
                    {PROJETOS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div style={s.campo}>
                <label style={s.label}>Equipe</label>
                <select style={s.input} value={formUsuario.equipe} onChange={e => setFormUsuario({...formUsuario, equipe: e.target.value})}>
                  <option value="">Sem equipe</option>
                  {equipesAtivas.filter(e => e.projeto === formUsuario.projeto).map(e => (
                    <option key={e.id} value={e.nome}>{e.nome}</option>
                  ))}
                </select>
              </div>
              <div style={s.campo}>
                <label style={s.label}>{modal.item ? 'Nova senha (deixe em branco para manter)' : 'Senha *'}</label>
                <input style={s.input} type="password" value={formUsuario.senha} onChange={e => setFormUsuario({...formUsuario, senha: e.target.value})} placeholder="••••••••" />
              </div>
              {erro && <p style={s.erro}>{erro}</p>}
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnCancelar} onClick={() => setModal({ aberto: false, item: null, tipo: null })}>Cancelar</button>
              <button style={s.btnSalvar} onClick={salvarUsuario} disabled={salvando}>
                <Check size={14} /> {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EQUIPE */}
      {modal.aberto && modal.tipo === 'equipe' && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitulo}>{modal.item ? 'Editar Equipe' : 'Nova Equipe'}</h2>
              <button style={s.btnFechar} onClick={() => setModal({ aberto: false, item: null, tipo: null })}><X size={18} /></button>
            </div>
            <div style={s.modalBody}>
              <div style={s.campo}>
                <label style={s.label}>Nome da equipe *</label>
                <input style={s.input} value={formEquipe.nome} onChange={e => setFormEquipe({...formEquipe, nome: e.target.value})} placeholder="Ex: Victor Lucas" />
              </div>
              <div style={s.campo}>
                <label style={s.label}>Projeto</label>
                <select style={s.input} value={formEquipe.projeto} onChange={e => setFormEquipe({...formEquipe, projeto: e.target.value})}>
                  {PROJETOS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              {erro && <p style={s.erro}>{erro}</p>}
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnCancelar} onClick={() => setModal({ aberto: false, item: null, tipo: null })}>Cancelar</button>
              <button style={s.btnSalvar} onClick={salvarEquipe} disabled={salvando}>
                <Check size={14} /> {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#080f1a', padding: '24px', fontFamily: 'sans-serif' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 16 },
  btnVoltar: { display: 'flex', alignItems: 'center', gap: 6, background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, padding: '8px 14px', color: '#94a3b8', cursor: 'pointer', fontSize: 13 },
  titulo: { color: '#f1f5f9', fontSize: 22, fontWeight: 700, margin: 0 },
  subtitulo: { color: '#475569', fontSize: 13, marginTop: 4 },
  btnNovo: { display: 'flex', alignItems: 'center', gap: 6, background: '#6366f1', border: 'none', borderRadius: 8, padding: '10px 18px', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  abas: { display: 'flex', gap: 6, marginBottom: 20 },
  aba: { background: '#0f172a', color: '#475569', border: '1px solid #1e293b', borderRadius: 8, padding: '8px 22px', cursor: 'pointer', fontSize: 14, fontWeight: 500 },
  abaAtiva: { background: '#6366f1', color: '#fff', border: '1px solid #6366f1' },
  busca: { width: '100%', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, padding: '10px 16px', color: '#f1f5f9', fontSize: 14, marginBottom: 16, boxSizing: 'border-box', outline: 'none' },
  tabela: { background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, overflow: 'hidden' },
  tabelaHeader: { display: 'flex', padding: '12px 16px', borderBottom: '1px solid #1e293b', background: '#080f1a' },
  th: { flex: 1, color: '#475569', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  row: { display: 'flex', padding: '14px 16px', borderBottom: '1px solid #080f1a', alignItems: 'center' },
  td: { flex: 1, color: '#64748b', fontSize: 13 },
  badge: { padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 },
  btnAcao: { background: '#1e293b', border: 'none', borderRadius: 6, padding: '6px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  vazio: { color: '#475569', textAlign: 'center', padding: 32 },
  loadingText: { color: '#475569', textAlign: 'center', padding: 32 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#0f172a', border: '1px solid #1e293b', borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #1e293b' },
  modalTitulo: { color: '#f1f5f9', fontSize: 18, fontWeight: 700, margin: 0 },
  btnFechar: { background: 'none', border: 'none', color: '#475569', cursor: 'pointer' },
  modalBody: { padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px', borderTop: '1px solid #1e293b' },
  campo: { display: 'flex', flexDirection: 'column', gap: 6 },
  campoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  label: { color: '#94a3b8', fontSize: 13, fontWeight: 500 },
  input: { background: '#080f1a', border: '1px solid #1e293b', borderRadius: 8, padding: '10px 14px', color: '#f1f5f9', fontSize: 14, outline: 'none' },
  dica: { color: '#f59e0b', fontSize: 11 },
  erro: { color: '#ef4444', fontSize: 13, margin: 0 },
  btnCancelar: { background: '#1e293b', border: 'none', borderRadius: 8, padding: '10px 18px', color: '#94a3b8', cursor: 'pointer', fontSize: 14 },
  btnSalvar: { display: 'flex', alignItems: 'center', gap: 6, background: '#6366f1', border: 'none', borderRadius: 8, padding: '10px 18px', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
};